import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, CartContainer, EntityList } from '@aimsos/ui';

import { Clock, Handshake, ListChecks, MessageSquareReply, ScanSearch, Sparkles, X } from 'lucide-react';

/** The icon of each task type; an unknown type degrades to the suggestion spark. */
const TYPE_ICON = {
  next_best_action: Sparkles,
  suggestion: Sparkles,
  handoff: Handshake,
  task: ListChecks,
  reply_needed: MessageSquareReply,
  fact_check: ScanSearch,
};

/** Types the Concierge suggested: their context line carries its spark, as in the prototype. */
const SUGGESTED_TYPES = new Set(['next_best_action', 'suggestion']);

/** A chip of the first line (owner, type, hint): the DS Tag, `Badge variant="neutral"`. */
const chip = (id, label) => ({
  id,
  label: (
    <Badge variant="neutral" size="small">
      <span data-testid="task-chip">{label}</span>
    </Badge>
  ),
});

/**
 * One task in the Tasks tab (CT-5722) — `CartContainer` + `EntityList`, the
 * pair every list here uses. The task maps onto `EntityList`'s slots:
 *
 *   type                 -> icon
 *   title                -> title
 *   owner · type · hint  -> primaryMeta, as chips ("Assigned to you · Handoff")
 *   description          -> description
 *   context · due        -> secondaryMeta (a suggestion's spark, the due clock)
 *   action               -> primaryCta (label by action TYPE)
 *   Not now, Dismiss…    -> the row's "…" menu (`dropdown`)
 *
 * `onAction(task)` and `onMenu(task, mutation)` are stable handlers; whether the
 * action is available (a Concierge that is not there yet, a Sandbox tab that
 * does not exist) is the caller's call, passed in as `actionState`.
 */
const TaskCard = memo(function TaskCard({ task, actionState = 'enabled', busy = false, onAction, onMenu }) {
  const { t } = useTranslation('unified-profiles');

  const handleAction = useCallback(() => onAction(task), [onAction, task]);
  const handleSnooze = useCallback(() => onMenu(task, 'snooze'), [onMenu, task]);
  const handleDismiss = useCallback(() => onMenu(task, 'dismiss'), [onMenu, task]);

  const view = useMemo(() => {
    let owner = null;
    if (task.owner) {
      owner = task.owner.isMe ? t('tasks.owner.me') : t('tasks.owner.other', { name: task.owner.name });
    }
    return {
      title: task.title,
      icon: TYPE_ICON[task.type] ?? Sparkles,
      iconHighlight: { bgColor: 'Purple', iconColor: 'Purple' },
      primaryMeta: [
        owner ? chip('owner', owner) : null,
        task.type ? chip('type', t(`tasks.types.${task.type}`)) : null,
        task.hint ? chip('hint', task.hint) : null,
      ].filter(Boolean),
      description: task.description ?? undefined,
      secondaryMeta: [
        task.contextLine
          ? { id: 'context', label: task.contextLine, icon: SUGGESTED_TYPES.has(task.type) ? Sparkles : undefined }
          : null,
        task.due?.label ? { id: 'due', label: task.due.label, icon: Clock } : null,
      ].filter(Boolean),
      primaryCta:
        task.action?.type && actionState !== 'hidden'
          ? {
              label: t(`tasks.actions.${task.action.type}`),
              variant: 'secondary',
              onClick: handleAction,
              disabled: busy || actionState === 'disabled',
            }
          : undefined,
      dropdown: {
        props: { triggerAriaLabel: t('tasks.menu.ariaLabel', { title: task.title }) },
        items: [
          {
            id: 'snooze',
            icon: Clock,
            label: (
              <span className="flex flex-col">
                <span>{t('tasks.menu.snooze')}</span>
                <span className="text-xs text-content-muted">{t('tasks.menu.snoozeHint')}</span>
              </span>
            ),
            onClick: handleSnooze,
          },
          { id: 'dismiss', icon: X, label: t('tasks.menu.dismiss'), onClick: handleDismiss },
        ],
      },
    };
  }, [task, actionState, busy, handleAction, handleSnooze, handleDismiss, t]);

  return (
    <div data-testid="task-card" data-task-id={task.id} data-task-type={task.type}>
      <CartContainer size="S" variant="neutral">
        <EntityList {...view} />
      </CartContainer>
    </div>
  );
});

export default TaskCard;
